import { useState, useEffect } from 'react';
import { Card, Form, InputNumber, Button, Alert, Typography, Space, Statistic } from 'antd';
import { DollarOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const { Title, Paragraph } = Typography;

interface BootstrapStatus {
  base_initialized: boolean;
  base_principal_cents: number;
  can_initialize: boolean;
  min_required_cents: number;
}

export function AdminBootstrap() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<BootstrapStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchBootstrapStatus();
  }, []);

  const fetchBootstrapStatus = async () => {
    try {
      setLoading(true);
      const data = await api.admin.getBootstrapStatus();
      setStatus({
        base_initialized: data.initialized,
        base_principal_cents: data.base_principal_cents || 0,
        can_initialize: !data.initialized,
        min_required_cents: 100000, // $1,000
      });
    } catch (err) {
      console.error('Failed to fetch bootstrap status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: { amount: number }) => {
    try {
      setSubmitting(true);
      setError(null);

      const data = await api.admin.bootstrapBase({
        base_principal_cents: Math.round(values.amount * 100),
      });

      if (data.message) {
        setSuccess(true);
        fetchBootstrapStatus(); // Refresh status
        form.resetFields();
        
        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to initialize base principal');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  if (loading) {
    return (
      <Card loading={true} style={{ maxWidth: 600, margin: '0 auto' }}>
        <div style={{ height: 200 }} />
      </Card>
    );
  }

  // Already initialized
  if (status?.base_initialized) {
    return (
      <Card style={{ maxWidth: 600, margin: '0 auto' }}>
        <Space orientation="vertical" size="large" style={{ width: '100%', textAlign: 'center' }}>
          <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a' }} />
          <Title level={3}>Base Principal Initialized</Title>
          <Statistic
            title="Locked Base Principal"
            value={formatCurrency(status.base_principal_cents)}
            prefix={<DollarOutlined />}
            styles={{ content: { color: '#3f8600', fontSize: 32 } }}
          />
          <Alert
            title="Base principal is locked"
            description="Your base principal can only be changed via user contributions or weekly reinvestments. Manual updates are not allowed."
            type="info"
            showIcon
          />
        </Space>
      </Card>
    );
  }

  // Can initialize
  if (status?.can_initialize) {
    return (
      <Card 
        style={{ maxWidth: 600, margin: '0 auto' }}
        title={
          <Space>
            <DollarOutlined style={{ fontSize: 24, color: '#1677ff' }} />
            <span>Initialize Admin Base Principal</span>
          </Space>
        }
      >
        <Space orientation="vertical" size="large" style={{ width: '100%' }}>
          <Alert
            title="Required: Set Starting Base Principal"
            description="As an admin, you must initialize your base principal before the system can function. This is a one-time operation."
            type="warning"
            showIcon
          />

          <div>
            <Paragraph>
              <strong>What is Base Principal?</strong>
            </Paragraph>
            <Paragraph type="secondary">
              Your base principal represents the admin's investment pool. It will be adjusted automatically when:
            </Paragraph>
            <ul style={{ color: 'rgba(255, 255, 255, 0.65)' }}>
              <li>New users contribute (offsets applied while maintaining $1,000 floor)</li>
              <li>Weekly reinvestments are processed (based on your reinvestment percentage)</li>
            </ul>
          </div>

          <Alert
            title={`Minimum Required: ${formatCurrency(status.min_required_cents)}`}
            type="info"
            showIcon
          />

          {success && (
            <Alert
              title="Base principal initialized successfully!"
              type="success"
              showIcon
              closable
              onClose={() => setSuccess(false)}
            />
          )}

          {error && (
            <Alert
              title={error}
              type="error"
              showIcon
              closable
              onClose={() => setError(null)}
            />
          )}

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{ amount: 1000 }}
          >
            <Form.Item
              name="amount"
              label="Base Principal Amount (USD)"
              rules={[
                { required: true, message: 'Please enter an amount' },
                {
                  type: 'number',
                  min: 1000,
                  message: 'Minimum amount is $1,000',
                },
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                size="large"
                prefix="$"
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => parseFloat(value!.replace(/\$\s?|(,*)/g, '')) as any}
                precision={2}
                min={1000}
                step={100}
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={submitting}
                icon={<DollarOutlined />}
              >
                Initialize Base Principal
              </Button>
            </Form.Item>
          </Form>

          <Alert
            title="Important"
            description="After initialization, your base principal cannot be manually edited. It will only change through system operations (contributions and reinvestments)."
            type="warning"
            showIcon
          />
        </Space>
      </Card>
    );
  }

  // Cannot initialize (not admin)
  return (
    <Card style={{ maxWidth: 600, margin: '0 auto' }}>
      <Alert
        title="Access Denied"
        description="Only admin users can initialize base principal."
        type="error"
        showIcon
      />
    </Card>
  );
}

export default AdminBootstrap;
